const TULUM_PARTY_BASE_URL = 'https://www.tulum.party';
const TULUM_TIMEZONE_OFFSET = '-05:00'; // Quintana Roo is UTC-5 year-round
import { venueDecoder } from '@/common/VenueDecoder';

export function eventScheduleSchemaGenerator(schedule) {

  const getSafe = (fn, defaultValue = undefined) => {
    try {
      const result = fn();
      return result === null || result === undefined ? defaultValue : result;
    } catch (e) {
      return defaultValue;
    }
  };

  function generateEventListItems() {
    if (!schedule || !Array.isArray(schedule) || schedule.length === 0) {
      return []; // Return empty array if no schedule data
    }

    const groupedEvents = {};

    schedule.forEach((party) => {
      const eventSlug = getSafe(() => party.metadata.event_link, '').trim();
      if (!eventSlug) {
        console.warn("Skipping party due to missing event_link:", party);
        return; // Skip if no event slug to group by
      }

      const fullTitle = getSafe(() => party.title, '');
      let venue = venueDecoder(party.title);

      const titleParts = fullTitle.split('@');
      const performerName = titleParts[0] ? titleParts[0].trim() : 'Unknown Performer';

      let eventName = party.metadata.event_name;
      let venueName = venue.name; // Default venue name

      const artistLinkPath = getSafe(() => party.metadata.artist_link, '').trim();
      const performer = {
        '@type': 'MusicGroup',
        name: performerName,
      };
      if (artistLinkPath.match('djs')) {
        // Assuming artist_link is like 'djs/artist-slug' (no leading slash)
        performer['@id'] = `${TULUM_PARTY_BASE_URL}${artistLinkPath}#entity`;
      }

      if (!groupedEvents[eventSlug]) {
        const date = getSafe(() => party.metadata.date);
        const startTime = getSafe(() => party.metadata.start_time);

        if (!date || !startTime) {
          console.warn("Skipping event group due to missing date or start_time:", party);
          return;
        }

        groupedEvents[eventSlug] = {
          // These are properties for the MusicEvent item
          '@type': 'MusicEvent',
          '@id': `${TULUM_PARTY_BASE_URL}${eventSlug.startsWith('/') ? eventSlug : '/' + eventSlug}#entity`,
          name: eventName,
          url: `${TULUM_PARTY_BASE_URL}${eventSlug.startsWith('/') ? eventSlug : '/' + eventSlug}`,
          startDate: `${date}T${startTime}${TULUM_TIMEZONE_OFFSET}`,
          location: {
            '@type': 'Place',
            name: venueName,
            '@id': `${TULUM_PARTY_BASE_URL}${venue.slug}#entity`,
          },
          performer: [performer], // Initialize with the first performer
          // Store original date/time for potential sorting if Object.values doesn't preserve order
          _originalSortKey: `${date}T${startTime}`,
        };
      } else {
        // Add performer if not already listed for this event (simple check by name for this example)
        if (!groupedEvents[eventSlug].performer.find(p => p.name === performerName)) {
          groupedEvents[eventSlug].performer.push(performer);
        }
      }
    });

    // Convert grouped events object to an array and sort by date/time
    const distinctEventsArray = Object.values(groupedEvents).sort((a, b) =>
      a._originalSortKey.localeCompare(b._originalSortKey)
    );

    return distinctEventsArray.map((eventData, idx) => {
      // Remove the temporary sort key before putting it in the schema
      const { _originalSortKey, ...itemData } = eventData;
      return {
        '@type': 'ListItem',
        position: idx + 1, // Position starts from 1
        item: itemData,
      };
    });
  }

  const eventListItems = generateEventListItems();

  return {
    '@context': 'https://schema.org',
    '@id': `${TULUM_PARTY_BASE_URL}/tulum_dj_schedule`,
    url: `${TULUM_PARTY_BASE_URL}/tulum_dj_schedule`,
    '@type': 'CollectionPage',
    name: 'Tulum DJ Schedule - Upcoming Music Events',
    description: "Discover all the DJs and electronic music artists playing next in Tulum. Your guide to Tulum\'s vibrant nightlife schedule.",
    mainEntity: {
    '@type': 'ItemList',
      'itemListOrder': 'http://schema.org/ItemListOrderAscending',
      'numberOfItems': eventListItems.length, // Correct number of distinct events
    'itemListElement': eventListItems,
  },
};
}
